import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import API from "../api/axiosConfig";
import { motion } from "framer-motion";
import { BadgeCheck, Search, Store, Loader2, ChevronLeft, X } from "lucide-react";
import { getImageUrl } from "../utils/getImageUrl";
import { isSedeOpen } from "../utils/sedeStatus";

const PAGE_SIZE = 24;

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function CompanyCard({ company, onClick }) {
  const isOpen = Array.isArray(company.sedes) && company.sedes.some((s) => isSedeOpen(s).open);
  const logo = company.logo ? getImageUrl(company.logo) : null;
  const initial = (company.name || "?")[0].toUpperCase();

  return (
    <motion.div
      onClick={onClick}
      whileHover={{ y: -3, boxShadow: "0 12px 28px rgba(0,0,0,0.10)" }}
      className="cursor-pointer bg-white rounded-2xl overflow-hidden border border-gray-100"
      style={{ boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}
    >
      <div className="relative h-36 bg-gray-50">
        {!isOpen && (
          <span className="absolute top-2 right-2 z-10 bg-black/60 text-white text-[9px] px-2 py-0.5 rounded-full">
            Cerrado
          </span>
        )}
        {logo ? (
          <img src={logo} alt={company.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100">
            <span className="text-4xl font-black text-gray-300">{initial}</span>
          </div>
        )}
      </div>
      <div className="px-3 py-2.5">
        <div className="flex items-center gap-1 mb-0.5">
          <p className="font-black text-sm text-gray-900 truncate">{company.name}</p>
          {company.verified && <BadgeCheck size={13} className="text-blue-500 shrink-0" />}
        </div>
        {company.category?.name && (
          <p className="text-[10px] text-gray-400 font-medium">{company.category.name}</p>
        )}
        {company.description && (
          <p className="text-[10px] text-gray-400 truncate mt-0.5 line-clamp-2">{company.description}</p>
        )}
      </div>
    </motion.div>
  );
}

export default function CompaniesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [companies, setCompanies] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [inputValue, setInputValue] = useState(searchParams.get("search") || "");
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get("categoryId") || "");
  const debounceRef = useRef(null);

  const fetchCompanies = useCallback(async (p = 1, q = search, cat = selectedCategory) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ showAll: "true", limit: String(PAGE_SIZE), page: String(p) });
      if (q.trim()) params.set("search", q.trim());
      if (cat) params.set("categoryId", cat);
      const res = await API.get(`/companies/public?${params}`);
      const raw = res.data?.data?.data ?? res.data?.data ?? res.data ?? [];
      const list = Array.isArray(raw) ? raw : [];
      // verified first, then shuffle within each group
      const verified = shuffle(list.filter((c) => c.verified));
      const others = shuffle(list.filter((c) => !c.verified));
      setCompanies([...verified, ...others]);
      setTotal(res.data?.data?.total ?? list.length);
      setPages(res.data?.data?.pages ?? 1);
    } catch {
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, [search, selectedCategory]);

  useEffect(() => {
    API.get("/categories")
      .then((res) => {
        const raw = res.data?.data?.categories ?? res.data?.categories ?? res.data ?? [];
        setCategories(Array.isArray(raw) ? raw : []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchCompanies(1, search, selectedCategory);
    // eslint-disable-next-line
  }, []);

  const applySearch = (q, cat) => {
    setPage(1);
    fetchCompanies(1, q, cat);
  };

  const handleInput = (val) => {
    setInputValue(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(val);
      applySearch(val, selectedCategory);
    }, 350);
  };

  const handleCategory = (cat) => {
    setSelectedCategory(cat);
    applySearch(search, cat);
  };

  const handlePage = (p) => {
    setPage(p);
    fetchCompanies(p, search, selectedCategory);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const totalPages = pages;

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-xl hover:bg-gray-100 transition-colors text-gray-500"
          >
            <ChevronLeft size={20} />
          </button>
          <div className="relative flex-1 max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar tiendas..."
              value={inputValue}
              onChange={(e) => handleInput(e.target.value)}
              className="w-full pl-8 pr-8 py-2 text-sm rounded-xl border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-supernova-400/30 focus:border-supernova-400"
            />
            {inputValue && (
              <button
                onClick={() => { setInputValue(""); handleInput(""); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <div className="text-xs text-gray-400 font-medium whitespace-nowrap">
            {loading ? "..." : `${total} tiendas`}
          </div>
        </div>

        {/* Category pills */}
        {categories.length > 0 && (
          <div className="max-w-5xl mx-auto px-4 pb-3 flex gap-2 overflow-x-auto no-scrollbar">
            <button
              onClick={() => handleCategory("")}
              className={`flex-shrink-0 text-xs font-bold px-3 py-1.5 rounded-full transition-colors ${
                !selectedCategory
                  ? "bg-supernova-500 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              Todas
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id ?? cat._id}
                onClick={() => handleCategory(cat.id ?? cat._id)}
                className={`flex-shrink-0 text-xs font-bold px-3 py-1.5 rounded-full transition-colors ${
                  selectedCategory === (cat.id ?? cat._id)
                    ? "bg-supernova-500 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {cat.icon && <span className="mr-1">{cat.icon}</span>}
                {cat.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Title */}
      <div className="max-w-5xl mx-auto px-4 pt-5 pb-2">
        <h1 className="text-xl font-black text-gray-900 tracking-tight">
          {selectedCategory
            ? `${categories.find((c) => (c.id ?? c._id) === selectedCategory)?.name ?? "Categoría"}`
            : "Todos los comercios"}
        </h1>
        <p className="text-xs text-gray-400 mt-0.5">
          {loading ? "Cargando..." : `${total} resultado${total !== 1 ? "s" : ""}`}
        </p>
      </div>

      {/* Grid */}
      <div className="max-w-5xl mx-auto px-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={28} className="animate-spin text-supernova-400" />
          </div>
        ) : companies.length === 0 ? (
          <div className="text-center py-20">
            <Store size={40} className="mx-auto mb-3 text-gray-200" />
            <p className="text-sm text-gray-400">No se encontraron tiendas</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mt-2">
            {companies.map((c, i) => (
              <CompanyCard
                key={c.id ?? c._id}
                company={c}
                onClick={() => navigate(`/company/${c.slug || c.id || c._id}`)}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8">
            <button
              onClick={() => handlePage(page - 1)}
              disabled={page === 1}
              className="px-3 py-1.5 rounded-xl text-xs font-bold bg-white border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition-colors"
            >
              ← Anterior
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                acc.push(p);
                return acc;
              }, [])
              .map((p, idx) =>
                p === "..." ? (
                  <span key={`e${idx}`} className="text-xs text-gray-400 px-1">…</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => handlePage(p)}
                    className={`w-8 h-8 rounded-xl text-xs font-bold transition-colors ${
                      p === page
                        ? "bg-supernova-500 text-white"
                        : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {p}
                  </button>
                )
              )}
            <button
              onClick={() => handlePage(page + 1)}
              disabled={page === totalPages}
              className="px-3 py-1.5 rounded-xl text-xs font-bold bg-white border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition-colors"
            >
              Siguiente →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
